<script setup>
// Grade de permissoes do perfil de acesso: modulos, paginas e acoes.
//
// Vive num componente proprio porque aparece em DOIS lugares com o mesmo
// desenho e regras diferentes de interacao: no modal de criar/editar ela e
// editavel, e no cartao de detalhes ela e so leitura. Duplicar o template
// deixaria as duas metades a divergir a cada ajuste de layout.
//
// Em `readonly` a coluna de MODULOS continua clicavel: escolher o modulo e
// navegacao, nao edicao — e sem ela nao daria para ver as paginas e acoes dos
// outros modulos.
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { ACCESS_MODULES, CONVERSATION_ACTIONS } from './accessProfilesCatalog';

const props = defineProps({
  selectedModuleId: { type: String, required: true },
  pagePermissions: { type: Array, default: () => [] },
  conversationActions: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
  labels: { type: Object, required: true },
});

const emit = defineEmits([
  'selectModule',
  'togglePage',
  'toggleAction',
  'toggleModule',
]);

const selectedModule = computed(() =>
  ACCESS_MODULES.find(module => module.id === props.selectedModuleId)
);

const selectedModuleAllowed = computed(() =>
  selectedModule.value.pages.some(page =>
    props.pagePermissions.includes(page.permission)
  )
);

// Fora do modulo de atendimentos so as duas acoes de acesso/visualizacao
// significam alguma coisa; as outras ficam desligadas por natureza.
const GENERIC_MODULE_ACTIONS = [
  'conversation_view',
  'conversation_page_access',
];

const actionAllowed = action => {
  if (props.selectedModuleId === 'conversations') {
    return props.conversationActions.includes(action.id);
  }
  return GENERIC_MODULE_ACTIONS.includes(action.id)
    ? selectedModuleAllowed.value
    : false;
};

const actionDisabled = action =>
  props.selectedModuleId !== 'conversations' &&
  !GENERIC_MODULE_ACTIONS.includes(action.id);

const moduleAllowed = module =>
  module.pages.some(page => props.pagePermissions.includes(page.permission));

const pageAllowed = page => props.pagePermissions.includes(page.permission);
</script>

<template>
  <div
    class="agents-page__permissions-grid mb-5 grid gap-4 max-md:gap-5 md:grid-cols-3"
  >
    <div
      class="agents-page__permissions-column agents-page__permissions-column--modules flex min-w-0 flex-col"
    >
      <span class="mb-1.5 text-body-mini font-medium text-n-slate-11">
        {{ labels.modulesLabel }}
      </span>
      <div
        class="agents-page__permissions-list flex min-h-64 flex-col rounded-xl border border-n-weak max-md:min-h-0"
      >
        <div
          v-for="module in ACCESS_MODULES"
          :key="module.id"
          class="agents-page__permission-item flex items-center gap-2.5 border-b border-n-weak px-3.5 py-2.5 text-left transition-colors last:border-b-0 ltr:border-l-2 rtl:border-r-2"
          :class="
            selectedModuleId === module.id
              ? 'bg-n-brand/5 text-n-blue-11 ltr:border-l-n-brand rtl:border-r-n-brand'
              : 'text-n-slate-11 hover:bg-n-alpha-1 ltr:border-l-transparent rtl:border-r-transparent'
          "
        >
          <button
            type="button"
            class="flex min-w-0 flex-1 items-center gap-2.5 text-left"
            @click="emit('selectModule', module)"
          >
            <Icon :icon="module.icon" class="size-[18px] flex-shrink-0" />
            <span class="truncate text-body-main">{{ module.label }}</span>
          </button>
          <input
            type="checkbox"
            class="size-4 accent-green-600"
            :aria-label="`Permitir módulo ${module.label}`"
            :checked="moduleAllowed(module)"
            :disabled="readonly"
            @change="emit('toggleModule', module, $event.target.checked)"
          />
        </div>
      </div>
    </div>

    <div
      class="agents-page__permissions-column agents-page__permissions-column--pages flex min-w-0 flex-col"
    >
      <span class="mb-1.5 text-body-mini font-medium text-n-slate-11">
        {{ labels.pagesLabel }}
      </span>
      <div
        class="agents-page__permissions-list flex min-h-64 flex-col rounded-xl border border-n-weak max-md:min-h-0"
      >
        <button
          v-for="page in selectedModule.pages"
          :key="page.id"
          type="button"
          :disabled="readonly"
          class="agents-page__permission-item flex items-center justify-between gap-2 border-b border-n-weak px-3.5 py-2.5 text-left text-body-main text-n-slate-11 transition-colors last:border-b-0"
          :class="
            readonly
              ? 'agents-page__permission-item--readonly cursor-default disabled:opacity-100'
              : 'hover:bg-n-alpha-1'
          "
          @click="emit('togglePage', page)"
        >
          <span class="truncate">{{ page.label }}</span>
          <Icon
            :icon="
              pageAllowed(page)
                ? 'i-lucide-circle-check'
                : 'i-lucide-circle-minus'
            "
            class="size-4 flex-shrink-0"
            :class="pageAllowed(page) ? 'text-n-brand' : 'text-n-slate-8'"
          />
        </button>
      </div>
    </div>

    <div
      class="agents-page__permissions-column agents-page__permissions-column--actions flex min-w-0 flex-col"
    >
      <span class="mb-1.5 text-body-mini font-medium text-n-slate-11">
        {{ labels.actionsLabel }}
      </span>
      <div
        class="agents-page__permissions-list flex min-h-64 flex-col rounded-xl border border-n-weak max-md:min-h-0"
      >
        <button
          v-for="action in CONVERSATION_ACTIONS"
          :key="action.id"
          type="button"
          :disabled="readonly || actionDisabled(action)"
          class="agents-page__permission-item flex items-center justify-between gap-2 border-b border-n-weak px-3.5 py-2.5 text-left transition-colors last:border-b-0"
          :class="
            readonly
              ? 'agents-page__permission-item--readonly cursor-default disabled:opacity-100'
              : 'hover:bg-n-alpha-1 disabled:cursor-not-allowed disabled:opacity-40'
          "
          @click="emit('toggleAction', action)"
        >
          <span class="text-body-main text-n-slate-11">
            {{ action.label }}
          </span>
          <Icon
            :icon="
              actionAllowed(action)
                ? 'i-lucide-square-check'
                : 'i-lucide-square'
            "
            class="size-[18px] flex-shrink-0"
            :class="actionAllowed(action) ? 'text-n-brand' : 'text-n-slate-8'"
          />
        </button>
      </div>
    </div>
  </div>
</template>
